import { useEffect, useMemo } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { ChevronRightRounded } from "@mui/icons-material";
import { useReducedMotion } from "framer-motion";
import { useParams } from "react-router-dom";
import { useSafeNavigate } from "../../../../hooks/useSafeNavigate";
import { useTouchLayout } from "../../../../hooks/useTouchLayout";
import { hapticSelection } from "../../../../lib/haptics";
import { isNative } from "../../../../lib/platform";
import BackButton from "../../../shared/BackButton";
import ErrorDisplay from "../../../common/ErrorDisplay/ErrorDisplay";
import { focusRingSx } from "../../overviewPanel";
import { CompanyIdentityRow } from "../../../polish/CompanyIdentityRow";
import {
  cardSx,
  TABULAR,
  tonalTokens,
  type TonalTokens,
} from "../../../polish/tokens";
import { EarningsLocked } from "../EarningsLocked";
import {
  earningsCompanyPath,
  earningsReportPath,
  REPORT_BACK_FALLBACK,
} from "../earningsNavigation";
import {
  useEarningsReport,
  isReportNotFound,
} from "../hooks/useEarningsReport";
import { MetricBars } from "../feed/MetricBars";
import {
  insightToneColour,
  SinceReport,
  sinceReportShown,
} from "../feed/SinceReport";
import { cardHeadingSx } from "../feed/headings";
import { ResultsCardSkeleton } from "../feed/FeedSkeletons";
import {
  formatQuote,
  metricComparisons,
  periodTitle,
  reportedOn,
  shortDate,
  signedPercent,
  signTone,
  targetMoves,
} from "../feed/feedModel";
import type {
  EarningsReport,
  EarningsReportHeader,
  SinceReport as SinceReportData,
} from "../feed/types";
import { PriceTargetsCard } from "./PriceTargetsCard";
import { ReportNotesTable } from "./ReportNotesTable";
import { PreviousReports } from "./PreviousReports";
import { NotesSkeleton, ReportSkeleton } from "./ReportSkeleton";

const GAP = { xs: 2, md: 3 } as const;

/** The page's column, as wide as the hub's. */
export const REPORT_PAGE_WIDTH = 1240;

const CARD_PADDING = {
  px: { xs: 2, md: 3 },
  py: { xs: 2, md: 2.5 },
} as const;

interface ReportHeaderCardProps {
  header: EarningsReportHeader;
  /** Null on a locked page or while it's withheld. */
  since: SinceReportData | null;
  tokens: TonalTokens;
  isDark: boolean;
  onOpenCompany: () => void;
}

const statLabelSx = {
  fontSize: { xs: "0.75rem", md: "0.8125rem" },
  lineHeight: "16px",
  fontWeight: 500,
  color: "text.secondary",
} as const;

/**
 * The report at a glance: the company (to its page) with the quarter and the
 * day it was reported opposite, then the price with today's move beside
 * Since report. Insight's own figure styles.
 */
const ReportHeaderCard = ({
  header,
  since,
  tokens,
  isDark,
  onOpenCompany,
}: ReportHeaderCardProps) => {
  const { company, quote } = header;
  const hasQuote = quote !== null && Number.isFinite(quote.price);
  const hasSince = sinceReportShown(since, company.priceCurrency);
  return (
    <Box
      component="section"
      aria-label={`${company.companyName} report`}
      data-testid="report-header"
      sx={{
        ...cardSx(tokens),
        ...CARD_PADDING,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Box
          component="button"
          type="button"
          aria-label={`Open ${company.companyName} earnings`}
          data-testid="report-identity"
          onClick={onOpenCompany}
          sx={{
            all: "unset",
            ...focusRingSx,
            display: "block",
            flex: "1 1 auto",
            minWidth: 0,
            borderRadius: "8px",
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
            "@media (hover: hover)": {
              "&:hover .identity-ticker": { color: tokens.gain },
            },
          }}
        >
          <CompanyIdentityRow
            symbol={company.symbol}
            name={company.companyName}
            exchange={company.exchange}
            large
          />
        </Box>
        <Box
          data-testid="report-period"
          sx={{ flex: "none", textAlign: "right" }}
        >
          {header.period !== null && (
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem" },
                lineHeight: "24px",
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: "text.primary",
              }}
            >
              {periodTitle(header.period.label)}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: { xs: "0.75rem", md: "0.8125rem" },
              lineHeight: "18px",
              color: "text.secondary",
            }}
          >
            {reportedOn(header.reportDate)}
          </Typography>
        </Box>
      </Box>

      {(hasQuote || hasSince) && (
        <Box
          data-testid="report-stats"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            mt: "auto",
            pt: { xs: 2.5, md: 3 },
          }}
        >
          {hasQuote && quote !== null && (
            <Box data-testid="report-quote">
              <Typography sx={statLabelSx}>Price</Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: { xs: "1.125rem", md: "1.3125rem" },
                    lineHeight: { xs: "24px", md: "28px" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  {formatQuote(quote.price, company.priceCurrency)}
                </Typography>
                {Number.isFinite(quote.changePct) && (
                  <Typography
                    data-testid="report-quote-change"
                    sx={{
                      fontSize: { xs: "0.8125rem", md: "0.875rem" },
                      fontWeight: 600,
                      color: insightToneColour(signTone(quote.changePct)),
                    }}
                  >
                    {signedPercent(quote.changePct)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {hasQuote && hasSince && (
            <Box
              aria-hidden
              sx={{
                flex: "none",
                width: "1px",
                height: 40,
                backgroundColor: tokens.hairline,
              }}
            />
          )}
          {hasSince && (
            <Box sx={{ ml: -0.5 }}>
              <SinceReport
                since={since}
                priceCurrency={company.priceCurrency}
                isDark={isDark}
                align="left"
                size="md"
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Beside this many price targets the targets card is about as tall as the
 * results card with its charts one above the other (≈ 450px), so they stack
 * there instead of leaving that height empty.
 */
export const STACK_CHARTS_FROM_TARGETS = 7;

interface ReportResultsCardProps {
  report: EarningsReport;
  tokens: TonalTokens;
  isDark: boolean;
  reduced: boolean;
  /** From md, EPS above revenue: the price targets beside are tall enough. */
  stacked: boolean;
}

/** EPS and revenue against their estimates, side by side or, beside a tall targets card, stacked. */
const ReportResultsCard = ({
  report,
  tokens,
  isDark,
  reduced,
  stacked,
}: ReportResultsCardProps) => {
  const metrics = metricComparisons({
    eps: report.eps,
    revenue: report.revenue,
    reportingCurrency: report.company.reportingCurrency,
  });
  return (
    <Box
      component="section"
      aria-labelledby="results-title"
      data-testid="report-results"
      sx={{ ...cardSx(tokens), ...CARD_PADDING }}
    >
      <Typography id="results-title" component="h2" sx={cardHeadingSx(tokens)}>
        Results
      </Typography>
      <Box
        data-testid="metrics"
        data-stacked={stacked}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: stacked ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
          },
          columnGap: { xs: 2, md: 4 },
          rowGap: 3,
          mt: { xs: 2, md: 2.5 },
        }}
      >
        {metrics.map((metric) => (
          <MetricBars
            key={metric.key}
            metric={metric}
            tokens={tokens}
            isDark={isDark}
            reduced={reduced}
            stackedFromMd={stacked}
          />
        ))}
      </Box>
    </Box>
  );
};

interface LatestReportChipProps {
  latest: NonNullable<EarningsReport["latestReport"]>;
  tokens: TonalTokens;
  onOpen: () => void;
}

/** This isn't the company's newest report: a way to it. */
const LatestReportChip = ({
  latest,
  tokens,
  onOpen,
}: LatestReportChipProps) => (
  <Box
    component="button"
    type="button"
    data-testid="latest-report"
    onClick={onOpen}
    sx={{
      all: "unset",
      ...focusRingSx,
      ...TABULAR,
      boxSizing: "border-box",
      display: "inline-flex",
      alignItems: "center",
      gap: 0.25,
      minHeight: 32,
      pl: 1.5,
      pr: 1,
      borderRadius: "999px",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: "0.8125rem",
      fontWeight: 500,
      color: tokens.text,
      backgroundColor: tokens.raised,
      WebkitTapHighlightColor: "transparent",
      "@media (pointer: coarse)": { minHeight: 44 },
      "@media (hover: hover)": { "&:hover": { color: tokens.gainText } },
    }}
  >
    {`Latest report · ${periodTitle(
      latest.periodLabel ?? shortDate(latest.reportDate),
    )}`}
    <ChevronRightRounded
      aria-hidden
      sx={{ fontSize: 18, color: tokens.faint }}
    />
  </Box>
);

/** A grid cell whose card fills it, so the two cards of a row end level. */
const cellSx = { minWidth: 0, display: "grid" } as const;

/** A grid row of up to two areas: both side by side, or the one alone across the row. */
const pairArea = (left: string | null, right: string | null): string | null => {
  if (left !== null && right !== null) return `"${left} ${right}"`;
  const only = left ?? right;
  return only === null ? null : `"${only} ${only}"`;
};

/**
 * /earnings/report/:eventId: one company's quarter. The header (the company,
 * the quarter and its day, the price and Since report) beside the reports
 * before it; then the results beside each firm's target move; then a row per
 * analyst note across the page. Narrow screens stack them, the earlier
 * reports last. Any member can open any report (B4).
 */
const EarningsReportPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tokens = tonalTokens(isDark);
  const touch = useTouchLayout();
  const reduced = useReducedMotion() === true;
  const native = isNative();
  const navigate = useSafeNavigate();
  const query = useEarningsReport(eventId);
  const data = query.data;

  // A report opens at its top, whether from deep in the feed or from another report.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [eventId]);

  const report = data !== undefined && !data.locked ? data.report : null;
  const latest = report?.latestReport ?? null;
  const moves = useMemo(
    () =>
      report === null ? null : targetMoves(report.notes, report.company.symbol),
    [report],
  );

  const goBack = () => {
    hapticSelection();
    const depth = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (depth > 0) {
      navigate(-1);
      return;
    }
    navigate(REPORT_BACK_FALLBACK.to, {
      replace: true,
      state: REPORT_BACK_FALLBACK.state,
    });
  };
  const goHub = () => {
    hapticSelection();
    navigate(REPORT_BACK_FALLBACK.to, { state: REPORT_BACK_FALLBACK.state });
  };
  const openCompany = (symbol: string) => {
    hapticSelection();
    navigate(earningsCompanyPath(symbol));
  };
  const openReport = (id: string) => {
    hapticSelection();
    navigate(earningsReportPath(id));
  };

  const notFound = query.isError && isReportNotFound(query.error);
  const hasResults =
    report !== null &&
    metricComparisons({
      eps: report.eps,
      revenue: report.revenue,
      reportingCurrency: report.company.reportingCurrency,
    }).length > 0;
  const hasTargets = (moves?.moves.length ?? 0) > 0;
  const hasNotes = report !== null && report.notes.length > 0;
  const hasPrevious = report !== null && report.previous.length > 0;

  const wideAreas = [
    pairArea("header", hasPrevious ? "previous" : null),
    pairArea(hasResults ? "results" : null, hasTargets ? "targets" : null),
    hasNotes ? `"notes notes"` : null,
  ]
    .filter((row): row is string => row !== null)
    .join(" ");
  const narrowAreas = [
    "header",
    hasResults && "results",
    hasTargets && "targets",
    hasNotes && "notes",
    hasPrevious && "previous",
  ]
    .filter((area): area is string => typeof area === "string")
    .map((area) => `"${area}"`)
    .join(" ");

  return (
    <Box
      data-testid="earnings-report-page"
      sx={{
        backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
        minHeight: "100dvh",
        px: { xs: 2, md: 3 },
        pt: { xs: 1.5, md: 2 },
        pb: 6,
      }}
    >
      <Box sx={{ maxWidth: REPORT_PAGE_WIDTH, mx: "auto" }}>
        {/* Native's top bar carries Back; the row stays only for the chip. */}
        {(!native || latest !== null) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              minHeight: 44,
              mb: { xs: 1, md: 1.5 },
            }}
          >
            {!native && (
              <BackButton
                onClick={goBack}
                sx={{ position: "static", ml: -1 }}
              />
            )}
            {native && <Box />}
            {latest !== null && (
              <LatestReportChip
                latest={latest}
                tokens={tokens}
                onOpen={() => openReport(latest.eventId)}
              />
            )}
          </Box>
        )}

        {query.isPending && <ReportSkeleton tokens={tokens} />}

        {notFound && (
          <Box data-testid="report-not-found">
            <ErrorDisplay variant="card" error={query.error} />
            <Button
              onClick={goHub}
              sx={{
                mt: 2,
                minHeight: 44,
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "999px",
                px: 2,
                color: tokens.muted,
                "@media (hover: hover)": {
                  "&:hover": {
                    color: tokens.text,
                    backgroundColor: tokens.raised,
                  },
                },
              }}
            >
              Back to Earnings Hub
            </Button>
          </Box>
        )}

        {query.isError && !notFound && data === undefined && (
          <ErrorDisplay
            variant="card"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        )}

        {data !== undefined && data.locked && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: GAP }}>
            <ReportHeaderCard
              header={data.report}
              since={null}
              tokens={tokens}
              isDark={isDark}
              onOpenCompany={() => openCompany(data.report.company.symbol)}
            />
            {!native && (
              <EarningsLocked tokens={tokens}>
                <ResultsCardSkeleton tokens={tokens} still />
                <NotesSkeleton tokens={tokens} still />
              </EarningsLocked>
            )}
          </Box>
        )}

        {report !== null && moves !== null && (
          <Box
            data-testid="report-grid"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "repeat(2, minmax(0, 1fr))",
              },
              gridTemplateAreas: { xs: narrowAreas, md: wideAreas },
              gap: GAP,
            }}
          >
            <Box sx={{ ...cellSx, gridArea: "header" }}>
              <ReportHeaderCard
                header={report}
                since={report.sinceReport}
                tokens={tokens}
                isDark={isDark}
                onOpenCompany={() => openCompany(report.company.symbol)}
              />
            </Box>
            {hasPrevious && (
              <Box sx={{ ...cellSx, gridArea: "previous" }}>
                <PreviousReports
                  previous={report.previous}
                  company={report.company}
                  tokens={tokens}
                  onOpenReport={openReport}
                />
              </Box>
            )}
            {hasResults && (
              <Box sx={{ ...cellSx, gridArea: "results" }}>
                <ReportResultsCard
                  report={report}
                  tokens={tokens}
                  isDark={isDark}
                  reduced={reduced}
                  stacked={moves.moves.length >= STACK_CHARTS_FROM_TARGETS}
                />
              </Box>
            )}
            {hasTargets && (
              <Box sx={{ ...cellSx, gridArea: "targets" }}>
                <PriceTargetsCard moves={moves} tokens={tokens} touch={touch} />
              </Box>
            )}
            {hasNotes && (
              <Box sx={{ ...cellSx, gridArea: "notes" }}>
                <ReportNotesTable
                  notes={report.notes}
                  symbol={report.company.symbol}
                  tokens={tokens}
                  touch={touch}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EarningsReportPage;
